import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from 'app/store/app.state';

import { ProjectLocationModel } from 'app/store/project-locations/project-locations.model';
import { selectProjectLocations } from 'app/store/project-locations/project-locations.selector';
import { clockIn } from 'app/store/timesheet/timesheet.action';
import { TimesheetTypes } from 'app/store/timesheet/timesheet.type';
import {
  checkIfCameraExists,
  getAllRouteParams,
} from 'app/utils/helpers/helper-functions';
import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';
import { CapturePhotoDialog } from '../capture-photo-dialog/capture-photo-dialog.component';
import { WebcamImage } from 'ngx-webcam';

@Component({
  selector: 'app-select-project-locations-dialog',
  templateUrl: './select-project-locations-dialog.component.html',
  styleUrl: './select-project-locations-dialog.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectProjectLocationsDialogComponent implements OnInit {
  private takePhotoDialogRef: MatDialogRef<CapturePhotoDialog> | null = null;
  selectedProjectLocation: ProjectLocationModel = {
    projectID: null,
    name: '',
  };
  projectLocationsList$: Observable<any[]> = of([]);
  companyID: any;
  webcamImage: WebcamImage | null = null;
  fileName: string = '';
  hasCamera: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<SelectProjectLocationsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<AppState>,
    private actions: Actions,
    private router: Router,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef,
  ) {}

  async ngOnInit() {
    this.hasCamera = await checkIfCameraExists();
    const routeParams = getAllRouteParams(this.route);
    this.companyID = routeParams['companyID'];
    this.projectLocationsList$ = this.store.select(selectProjectLocations).pipe(
      map((data) => (Array.isArray(data) ? data : [])),
      tap(() => {
        this.cdr.markForCheck();
      }),
      catchError(() => {
        return of([]);
      }),
    );

    this.projectLocationsList$.subscribe((locations) => {
      console.log('Subscription - Project locations:', locations);
    });

    this.dialogRef.backdropClick().subscribe(() => {
      this.data.loading$.next(false);
    });
  }

  onHandleSelectProjectLocation(selectedVal: ProjectLocationModel) {
    if (!selectedVal) return;
    this.selectedProjectLocation = selectedVal;
    if (selectedVal?.project_selfie_enabled && this.hasCamera) {
      if (this.takePhotoDialogRef && this.takePhotoDialogRef.getState() !== 0) {
        return;
      }

      this.openCapturePhotoDialog(selectedVal);
     
    } else {
      this.webcamImage = null;
      this.fileName = '';
      this.cdr.markForCheck();
    }
  }

  openCapturePhotoDialog(selectedVal: ProjectLocationModel) {
    const existingDialog = this.dialog.getDialogById('capture-photo-dialog');

    if (!existingDialog) {
      const takePhotoDialog = this.dialog.open(CapturePhotoDialog, {
        disableClose: true,
        id: 'capture-photo-dialog',
        data: {
          selectedVal,
        },
      });

      takePhotoDialog.afterClosed().subscribe((result) => {
        this.webcamImage = result.webcamImage;
        this.fileName = result.fileName;
        this.cdr.markForCheck();
      });
    }
  }

  onHandleSubmit() {
    this.dialog.closeAll();
    this.data.loading$.next(true);

    const data = {
      projectId: this.selectedProjectLocation.projectID!,
      companyID: this.companyID,
      ...(this.fileName && { clockInSelfie: this.fileName }),
    };
    this.store.dispatch(clockIn(data));

    this.actions
      .pipe(
        ofType(
          TimesheetTypes.POST_CLOCK_IN_SUCCESS,
          TimesheetTypes.POST_CLOCK_IN_CANCEL,
        ),
        tap(() => {
          this.data.loading$.next(false);
          this.dialog.closeAll();
          this.router.navigate([`${this.companyID}/dashboard`]);
        }),
      )
      .subscribe();
  }
}
